import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllOrdersRequest,
  getAllOrdersSuccess,
} from "../../../redux/orders/slice";
import dayjs from "dayjs";
import OrderTableFlagsCell from "../../../components/order-table/flags-cell";
import OrderTableOrderIdCell from "../../../components/order-table/orderId-cell";
import OrderTableRushCell from "../../../components/order-table/rush-cell";
import OrderTableClientCell from "../../../components/order-table/client-cell";
import OrderTableStatusCell from "../../../components/order-table/status-cell";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import { getInspectionServicesRequest } from "../../../redux/inspection-firms/slice";
import TableHeaderDatePicker from "../../../components/table-header-datepicker/table-header-datepicker";
import { queueType, userType } from "../../../utils/Constant";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import AddInspectorAndClientIssueDialog from "../issues/add-inspector-client-issue/add-inspector-client-issue";
import AddIssueDialog from "../issues/add-issue/add-issue";
import {
  reviewOrderRequest,
  deleteOrderRequest,
  updateQuickNoteRequest,
} from "../../../utils/APICalls";
import Notification from "../../../components/notification/notification";
import ErrorNotification from "../../../components/error-notification/error-notification";
import DeleteOrderDialog from "../../../components/delete-order-dialog/delete-order-dialog";
import Loading from "../../../components/loading/loading";
import QuickNoteEditCell from "../../../components/order-table/note-edit-cell";

export default function OrderReview() {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({
    queueType: queueType.created,
    orderStatus: "OrderReview",
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [inspectionType, setInspectionType] = useState<any[]>([]);
  const [showAddIssue, setShowAddIssue] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { loading, allOrders } = useSelector((state: any) => state.orders);
  const { inspectionServices } = useSelector(
    (state: any) => state.inspectionFirms
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  const rowCountRef = useRef(allOrders?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (allOrders?.totalCount !== undefined) {
      rowCountRef.current = allOrders?.totalCount;
    }
    return rowCountRef.current;
  }, [allOrders?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "flags",
      headerName: "Flags",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableFlagsCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Flags</Typography>
      ),
    },
    {
      field: "orderNumber",
      headerName: "Order ID",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableOrderIdCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="orderNumber"
          sortField="orderid"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "orderStatus",
      headerName: "Status",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableStatusCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="orderStatus"
          sortField="status"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["OrderAdded", "OrderReview", "OrderPending"]}
          disabled={true}
        />
      ),
    },
    {
      field: "isRushInspection",
      headerName: "Rush?",
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableRushCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          field="isRushInspection"
          sortField="isrushinspection"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Yes", "No"]}
        />
      ),
    },
    ...(currentUser?.userType === userType.tenant
      ? [
          {
            field: "quickNote",
            headerName: "Quick Note",
            width: 180,
            sortable: false,
            editable: true,
            renderEditCell: (params: any) => (
              <QuickNoteEditCell params={params} isEdit={true} />
            ),
            renderCell: (params: any) => (
              <QuickNoteEditCell params={params} isEdit={false} />
            ),
            renderHeader: (params: any) => (
              <TableHeaderTextField
                field="quickNote"
                sortField="quicknote"
                headerName={params.colDef.headerName}
                width={params.colDef.computedWidth}
                showFilter={showTableFilter}
                onFilterSubmit={onFilterUsers}
              />
            ),
          },
        ]
      : []),
    {
      field: "client",
      headerName: "Client",
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <OrderTableClientCell rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="clientName"
          sortField="clientname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    ...(currentUser?.userType === userType.tenant
      ? [
          {
            field: "inspectionType",
            headerName: "Inspection Type",
            width: 180,
            sortable: false,
            valueFormatter: (value?: string) => {
              const findType = inspectionServices.find(
                (t: any) => t?.inspectionType === value
              );
              return findType ? findType?.displayName : "-";
            },
            renderHeader: (params: any) => (
              <TableHeaderSelectField
                field="inspectionType"
                sortField="inspectiontype"
                headerName={params.colDef.headerName}
                width={params.colDef.computedWidth}
                showFilter={showTableFilter}
                onFilterSubmit={onFilterUsers}
                values={inspectionType}
              />
            ),
          },
        ]
      : []),
    {
      field: "clientDueDate",
      headerName: "Client Due Date",
      width: 180,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="clientDueDate"
          sortField="clientduedate"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "eta",
      headerName: "ETA",
      width: 100,
      sortable: false,
      valueGetter: (value, row) => {
        return (
          Math.ceil(dayjs(row?.clientDueDate).diff(Date.now(), "day", true)) ||
          ""
        );
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>ETA</Typography>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 180,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.street || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="address"
          sortField="address"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.city || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.state || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.propertyRecord?.zip || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactName",
      headerName: "Property Contact Name",
      width: 240,
      sortable: false,
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].name
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactName"
          sortField="propertycontactname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "propertyContactPhone",
      headerName: "Property Contact Phone",
      width: 240,
      sortable: false,
      valueGetter: (value, row) => {
        return row.propertyContacts && row.propertyContacts.length > 0
          ? row.propertyContacts[0].phone
          : "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="propertyContactPhone"
          sortField="propertycontactphone"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "inspectorFirm",
      headerName: "Inspector",
      width: 220,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.inspectorFirm?.displayName || "";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="inspectorFirmName"
          sortField="inspectorfirmname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    ...(currentUser?.userType !== userType.client
      ? [
          {
            field: "daysInQueue",
            headerName: "Days In Queue",
            width: 180,
            sortable: false,
            renderHeader: () => (
              <Typography sx={{ fontWeight: "bold" }}>Days In Queue</Typography>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    dispatch(getAllOrdersSuccess({ response: null }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const inspectionType = inspectionServices.map((service: any) => ({
        value: service?.inspectionType,
        label: service?.displayName,
      }));
      setInspectionType(inspectionType);
    }
  }, [inspectionServices]);

  useEffect(() => {
    setRowData(allOrders?.items);
  }, [allOrders]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      dispatch(getAllOrdersRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const handleReviewComplete = async () => {
    if (rowSelectionModel.length !== 1) return;

    setIsProcessing(true);
    const orderId = rowSelectionModel[0] as string;
    const response = await reviewOrderRequest(orderId);
    setIsProcessing(false);

    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage("Order reviewed successfully.");
      setShowSuccessNotification(true);
      setRowSelectionModel([]);
      // Refresh the orders list
      dispatch(getAllOrdersRequest(filterParams));
    } else if (response?.status === 400 || response?.status === 403) {
      setErrorMessage(response?.data?.detail || "Failed to review order.");
      setShowErrorNotification(true);
    } else {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    }
  };

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) return;
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (rowSelectionModel.length === 0) return;

    setIsProcessing(true);
    const orderIds = rowSelectionModel as string[];

    // Delete all selected orders
    const response = await deleteOrderRequest(orderIds);
    setIsProcessing(false);

    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage(
        orderIds.length === 1
          ? "Order deleted successfully."
          : `${orderIds.length} orders deleted successfully.`
      );
      setShowSuccessNotification(true);
      setRowSelectionModel([]);
      setShowDeleteDialog(false);
      // Refresh the orders list
      dispatch(getAllOrdersRequest(filterParams));
    } else {
      setErrorMessage("Failed to delete orders. Please try again.");
      setShowErrorNotification(true);
      setShowDeleteDialog(false);
    }
  };

  const updateQuickNote = async (orderId: string, note: string) => {
    const payload = {
      quickNote: note,
    };
    setShowLoading(true);
    const response = await updateQuickNoteRequest(orderId, payload);
    setShowLoading(false);
    if (response?.status === 200 || response?.status === 204) {
      setSuccessMessage("Updated Quick Note Successfully.");
      setShowSuccessNotification(true);
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      queueType: queueType.created,
      orderStatus: "OrderReview",
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {currentUser?.userType === userType.tenant ? (
            <>
              <Button
                sx={{ fontWeight: "550" }}
                variant="text"
                disabled={rowSelectionModel.length !== 1 || isProcessing}
                onClick={handleReviewComplete}
              >
                Review Complete
              </Button>
              <Divider orientation="vertical" sx={{ height: 34, mx: 0.5 }} />
              <IconButton
                aria-label="issue"
                disabled={rowSelectionModel.length === 0}
                onClick={() => setShowAddIssue(true)}
              >
                <FeedbackOutlinedIcon
                  color={
                    rowSelectionModel.length === 0 ? "disabled" : "primary"
                  }
                />
              </IconButton>
              <IconButton
                aria-label="delete"
                disabled={rowSelectionModel.length === 0 || isProcessing}
                onClick={handleDeleteClick}
              >
                <DeleteOutlinedIcon
                  color={rowSelectionModel.length === 0 ? "disabled" : "error"}
                />
              </IconButton>
            </>
          ) : (
            <IconButton
              aria-label="issue"
              disabled={rowSelectionModel.length === 0}
              onClick={() => setShowAddIssue(true)}
            >
              <FeedbackOutlinedIcon
                color={rowSelectionModel.length === 0 ? "disabled" : "primary"}
              />
            </IconButton>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableVirtualization={true}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
            const selected = newRowSelectionModel.map((id: string) =>
              rowData.find((r: any) => r.id === id)
            );
            setSelectedRowData(selected);
          }}
          rowSelectionModel={rowSelectionModel}
          processRowUpdate={(updatedRow, originalRow) => {
            if (originalRow?.quickNote !== updatedRow.quickNote) {
              updateQuickNote(updatedRow.id, updatedRow.quickNote);
            }
            setRowData((prev: any) =>
              prev.map((r: any) => (r.id === updatedRow.id ? updatedRow : r))
            );
            return updatedRow;
          }}
        />
      </Box>

      {showAddIssue && [
        currentUser?.userType === userType.tenant ? (
          <AddIssueDialog
            orders={selectedRowData}
            showDialog={showAddIssue}
            closeDialog={() => setShowAddIssue(false)}
          />
        ) : (
          <AddInspectorAndClientIssueDialog
            orders={selectedRowData}
            showDialog={showAddIssue}
            closeDialog={() => setShowAddIssue(false)}
          />
        ),
      ]}

      {/* Delete Confirmation Dialog */}
      <DeleteOrderDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        count={rowSelectionModel.length}
        isProcessing={isProcessing}
      />

      {/* Success Notification */}
      <Notification
        show={showSuccessNotification}
        onClose={() => setShowSuccessNotification(false)}
        title="Success"
        message={successMessage}
      />

      {/* Error Notification */}
      <ErrorNotification
        show={showErrorNotification}
        onClose={() => setShowErrorNotification(false)}
        title="Error"
        message={errorMessage}
      />

      {showLoading && <Loading />}
    </>
  );
}
